<template>
    <popup-modal
        custom-classes="w-768"
        :close-on-click="true"
        :hasCloseButton="true"
        :title="$t('question.manage').toString()"
        @close="$emit('close')"
    >
        <loading v-if="fetchingQuestions" class="pt-16"></loading>
        <draggable-transition-group
            v-else
            label="question"
            custom-styling="mb-32"
            translatable
            :options="questions"
            :draggable="false"
            @editing="editQuestion($event)"
            @deleting="deleting = $event"
        ></draggable-transition-group>

        <edit-resource
            v-if="editing"
            custom-class="w-768"
            translatable
            label="question"
            :form="editingForm"
            :title="$t('question.manage').toString()"
            @close="stopEditing"
            @submit="saveQuestion"
        >
            <div class="w-600">
                <simple-input :static-id="'manageQuestionsInput'" v-model="editingForm.question[locale]"/>
            </div>
        </edit-resource>

        <popup-modal-confirm
            v-if="deleting"
            :confirm-text="$t('general.delete_subject', {subject: $t('settings.questions.single')})"
            :message="$t('question.delete_confirm').toString()"
            show-exclamation
            :working="performingDeleteRequest"
            @confirm="deleteQuestion(deleting)"
            @close="cancelDelete"
        />
    </popup-modal>
</template>

<script>
import DraggableTransitionGroup from "./ui/DraggableTransitionGroup.vue";
import Loading from "./ui/Loading.vue";
import * as questionService from "../services/QuestionService";
import Question from "../models/Question";
import {normalizeJson} from "../../shared/helpers";
import {mapState, mapActions} from "pinia";
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../state/store';
import {useNotificationsStore} from '../state/modules/notifications';
import PopupModal from "./ui/PopupModal.vue";
import ModuleTemplateEditor from "./ui/ModuleTemplateEditor.vue";
import PopupModalConfirm from "./ui/PopupModalConfirm.vue";
import EditResource from "./ui/EditResource.vue";
import Form from "../etc/forms/Form";
import SimpleInput from "./forms/Input.vue";
import * as JobReasonService from "../services/JobReasonService";
import JobReason from "../models/JobReason";
import {ADD_NOTIFICATION} from "../state/mutation-types";

function getFields () {
    return [
        { name: 'question', value: {} },
    ];
}

export default {
    name: "Questions",
    components: {EditResource, PopupModalConfirm, ModuleTemplateEditor, PopupModal, Loading, DraggableTransitionGroup, SimpleInput},
    data() {
        return {
            fetchingQuestions: true,
            editing: null,
            editingForm: null,
            deleting: null,
            performingDeleteRequest: false,
        }
    },
    computed: {
        ...mapState(useMainStore, ['selectedLocale']),
        locale() {
            return this.selectedLocale;
        },
        questions() {
            return useRepo(Question)
                .orderBy('created_at', 'desc')
                .get();
        },
        service() {
            return questionService;
        }
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        fetchQuestions() {
            questionService.index()
                .then(({data}) => {
                    return useRepo(Question).save(normalizeJson(data));
                })
                .then((inserts) => {
                    this.fetchingQuestions = false;
                });
        },
        generateForm() {
            let fields = getFields();

            if (this.editing) {
                fields.forEach((field, index) => {
                    fields[index].value = JSON.parse(JSON.stringify(this.editing[field.name]));
                });
            }

            return new Form(fields);
        },
        editQuestion(question) {
            this.editing = question;
            this.editingForm = this.generateForm();
        },
        stopEditing() {
            this.editing = null;
            this.editingForm = null;
        },
        saveQuestion() {
            this.editingForm.submit((data) => {
                return questionService.update(this.editing.id, {
                    data: {attributes: {...data}}
                });
            }).then(async (data) => {
                return useRepo(Question).save(normalizeJson(data));
            }).then(() => this.stopEditing());
        },
        deleteQuestion(question) {
            this.performingDeleteRequest = true;

            questionService.destroy(question.id).then(async () => {
                return useRepo(Question).destroy(question.id);
            }).then(() => {
                this.deleting = null;
                return this.performingDeleteRequest = false;
            }).then(() => {
                this.$emit('delete');
                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('general.deleted', {subject: this.$t('settings.reject_reasons.single')}),
                });
            });
        },
        cancelDelete() {
            this.deleting = null;
        },
    },
    mounted() {
        this.fetchQuestions();
    }
}
</script>
